import {forwardRef, useCallback, useImperativeHandle, useMemo, useReducer, useRef, useState} from "react";
import {Observer} from "mobx-react";
import type {IDropdownOption} from "../../../interaction/DropdownOptionsV5";
import type {Markup} from "../../../../../data/models/Markup";
import type {Markup3D} from "../../../../modules/space/spaceeditor/logic3d/elements3d/markups/abstract/Markup3D";
import type {Xyicon} from "../../../../../data/models/Xyicon";
import type {Xyicon3D} from "../../../../modules/space/spaceeditor/logic3d/elements3d/Xyicon3D";
import type {Color} from "../../../../../generated/api/base";
import type {SpaceViewRenderer} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import type {SelectionTool} from "../../../../modules/space/spaceeditor/logic3d/features/tools/SelectionTool";
import {Debouncer} from "../../../../../utils/function/Debouncer";
import {Permission} from "../../../../../generated/api/base";
import {HorizontalAlignment, VerticalAlignment} from "../../../../../utils/dom/DomUtils";
import {KeyboardListener} from "../../../../../utils/interaction/key/KeyboardListener";
import {PointerDetector} from "../../../../../utils/interaction/PointerDetector";
import {ColorSelectorV5} from "../../../colors/ColorSelectorV5";
import {DropdownButtonV5} from "../../../interaction/DropdownButtonV5";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import {Functions} from "../../../../../utils/function/Functions";
import {FIND_XYICONS_WINDOW_SIZE, FindXyiconsWindowV5} from "../links/FindXyiconsWindowV5";
import {MarkupsWithCustomizableColor} from "../../../../modules/space/spaceeditor/logic3d/elements3d/markups/MarkupStaticElements";
import ApplyIcon from "../../../icons/check.svg?react";
import CancelIcon from "../../../icons/xmark-large.svg?react";
import RedrawIcon from "../../../icons/boundary-redraw.svg?react";
import EditIcon from "../../../icons/markup-pencil.svg?react";
import SearchIcon from "../../../icons/search.svg?react";
import CopyIcon from "../../../icons/copy.svg?react";
import TextIcon from "../../../icons/markup-text.svg?react";
import LinkIcon from "../../../icons/link.svg?react";
import DeleteIcon from "../../../icons/trash.svg?react";
import MoreIcon from "../../../icons/dots-horizontal.svg?react";
import MergeIcon from "../../../icons/merge.svg?react";
import StampIcon from "../../../icons/stamp.svg?react";
import ScissorsIcon from "../../../icons/scissors.svg?react";
import UnplotIcon from "../../../icons/unplotted-xyicon.svg?react";
import FlipHorizontalIcon from "../../../icons/flip-horizontal.svg?react";
import FlipVerticalIcon from "../../../icons/flip-vertical.svg?react";
import InfoIcon from "../../../icons/info.svg?react";
import LockIcon from "../../../icons/lock-alt.svg?react";
import UnlockIcon from "../../../icons/unlock-alt.svg?react";
import {notifyAboutInsufficientPermissions, onUnplotClick} from "../../ActionBarUtils";
import {useCloseOnKeyboardPress} from "../../../utils";
import {
	doesMarkupKeepEditButtonInV5,
	getWorldSizeFromPixels,
} from "../../../../modules/space/spaceeditor/logic3d/managers/spaceitems/SelectionHandleUtils";
import {getFloatingOverlayStyle, useFloatingOverlayPosition} from "../../../../modules/space/spaceeditor/ui/FloatingOverlayUtils";
import {MarkupTypeChangerV5} from "./MarkupTypeChangerV5";
import {SelectedObjectsBubble} from "./SelectedObjectsBubble";
import {SpaceActionBarStyled} from "./SpaceActionBar.styled";
import {MarkupTextModifierV5} from "./MarkupTextModifierV5";
import {ArrowHeadSizeChangerV5} from "./ArrowHeadSizeChangerV5";
import {LineThicknessChangerV5} from "./LineThicknessChangerV5";
import {FillButtonV5} from "./FillButtonV5";
import {Photo360PreviewButtonV5} from "./Photo360PreviewButtonV5";

interface ISpaceActionBarProps {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly isEditEnabled: boolean;
	readonly isInEditMode: boolean;
	readonly isLinkEnabled: boolean;
	readonly getWorldPosition: () => {x: number; y: number};
	readonly isDetailsPanelOpen: boolean;
	readonly onDeleteClick: () => void;
	readonly onRedrawClick: () => void;
	readonly onCancelEditClick: () => void;
	readonly onApplyEditClick: () => void;
	readonly openMergeBoundariesWindow: () => void;
	readonly toggleDetailsPanel: () => void;
}

export interface ISpaceActionBarV5Ref {
	onAddTextClick: () => void;
	onTextEditorClose: () => void;
}

const _markupColorChangeDebouncer: Debouncer = new Debouncer();
const ACTION_BAR_VERTICAL_OFFSET_PX = 30;

export const SpaceActionBarV5 = forwardRef<ISpaceActionBarV5Ref, ISpaceActionBarProps>((props, ref) => {
	const {spaceViewRenderer, openMergeBoundariesWindow} = props;
	const {spaceItemController, actions} = spaceViewRenderer;
	const {selectedItems} = spaceItemController;
	const [isTextEditorOpen, setIsTextEditorOpen] = useState<boolean>(false);
	const [isInFindXyiconsMode, setIsInFindXyiconsMode] = useState<boolean>(false);
	const {linkManager} = spaceViewRenderer.toolManager;
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const _ref = useRef<HTMLDivElement>(null);
	const positionSignals = useMemo(() => [spaceItemController.signals.actionBarUpdated], [spaceItemController]);

	const _isSingleMarkupSelected: boolean = spaceItemController.markupManager.selectedItems.length === 1 && selectedItems.length === 1;
	const _itemsWithoutPermissionCount: number = actions.getNumberOfSpaceItemsWithoutPermission(selectedItems, Permission.Update);

	const getActionBarWorldPosition = () => {
		const currentPosition = props.getWorldPosition();
		let worldY =
			currentPosition.y +
			getWorldSizeFromPixels(
				ACTION_BAR_VERTICAL_OFFSET_PX,
				spaceViewRenderer.correctionMultiplier.current,
				spaceViewRenderer.toolManager.cameraControls.cameraZoomValue,
			);

		if (spaceItemController.isInEditMode && selectedItems.every((m) => m.spaceItemType === "markup" && (m as Markup3D).isTextOffsetHandlerVisible)) {
			worldY += 100 * spaceViewRenderer.correctionMultiplier.current;
		}

		return {
			x: currentPosition.x,
			y: worldY,
		};
	};

	useFloatingOverlayPosition({
		elementRef: _ref,
		spaceViewRenderer,
		getWorldPosition: getActionBarWorldPosition,
		position: "top",
		signals: positionSignals,
	});

	const onPointerUpCapture = () => {
		const activeTool = spaceViewRenderer.toolManager.activeTool;

		if (activeTool?.toolId === "selection") {
			(activeTool as SelectionTool).releaseInteractionFromOverlay?.();
		}
	};

	const onLinkClick = () => {
		const xyicon = (selectedItems[0] as Xyicon3D).modelData as Xyicon;
		const ports = xyicon.ports;
		const worldPosition = props.getWorldPosition();

		if (_itemsWithoutPermissionCount === 0) {
			if (selectedItems.length === 1 && ports.length > 0) {
				spaceItemController.openPortSelector(worldPosition.x, worldPosition.y, xyicon, ports, "from");
			} else {
				linkManager.enableLinkMode(null);
			}
			forceUpdate();

			spaceViewRenderer.inheritedMethods.setActiveTool("selection");
		} else {
			notifyAboutInsufficientPermissions(spaceViewRenderer);
		}
	};

	useCloseOnKeyboardPress(() => setIsInFindXyiconsMode(false), KeyboardListener.KEY_ESCAPE);

	const onCancelLinkClick = useCallback(() => {
		linkManager.disableLinkMode();
		setIsInFindXyiconsMode(false);
		forceUpdate();
	}, [linkManager]);

	const onFindXyiconClick = () => {
		setIsInFindXyiconsMode(true);
	};

	const onFlipXClick = () => {
		spaceItemController.xyiconManager.flipXSelected();
	};

	const onFlipYClick = () => {
		spaceItemController.xyiconManager.flipYSelected();
	};

	const onAddTextClick = useCallback(() => {
		setIsTextEditorOpen(true);
		spaceViewRenderer.inheritedMethods.setActiveTool("selection");
	}, [spaceViewRenderer]);

	const onTextEditorClose = useCallback(() => {
		setIsTextEditorOpen(false);
	}, []);

	useImperativeHandle(
		ref,
		() => ({
			onAddTextClick,
			onTextEditorClose,
		}),
		[onAddTextClick, onTextEditorClose],
	);

	const onEditClick = () => {
		spaceItemController.switchEditMode(!props.isInEditMode);
	};

	return (
		<Observer>
			{() => {
				const areOnlyMarkupsSelected = selectedItems.length === spaceViewRenderer.markupManager.selectedItems.length;
				const firstColoredMarkup = spaceViewRenderer.markupManager.selectedItems.find((m: Markup3D) =>
					MarkupsWithCustomizableColor.includes(m.type),
				) as Markup3D;

				const singleMarkup = spaceItemController.markupManager.selectedItems[0] as Markup3D;
				const doesMarkupSupportText = singleMarkup?.doesSupportText;
				const doesSupportText = _isSingleMarkupSelected && doesMarkupSupportText;

				const hasPermissionToUpdate = hasPermission(spaceViewRenderer, Permission.Update);
				const hasPermissionToDelete = hasPermission(spaceViewRenderer, Permission.Delete);
				const elementSize = isInFindXyiconsMode ? FIND_XYICONS_WINDOW_SIZE : undefined;
				const style = getFloatingOverlayStyle({
					spaceViewRenderer,
					getWorldPosition: getActionBarWorldPosition,
					position: "top",
					element: _ref.current,
					elementSize,
				});

				const isAnySelectedLocked = selectedItems.some((i) => i.isPositionLockedAnyWay);

				return doesSupportText && isTextEditorOpen ? (
					<MarkupTextModifierV5
						barRef={_ref}
						style={style}
						spaceViewRenderer={spaceViewRenderer}
						markup3D={singleMarkup}
						onClose={onTextEditorClose}
					/>
				) : isInFindXyiconsMode ? (
					<FindXyiconsWindowV5
						divRef={_ref}
						onClose={onCancelLinkClick}
						style={style}
					/>
				) : (
					<SpaceActionBarStyled
						ref={_ref}
						className="SpaceActionBar"
						style={style}
						onContextMenu={PointerDetector.onContextMenu}
						onPointerUpCapture={onPointerUpCapture}
					>
						{props.isInEditMode ? (
							<>
								{spaceViewRenderer.boundaryManager.isInEditMode && (
									<IconButtonV5
										IconComponent={RedrawIcon}
										title="Redraw"
										onClick={props.onRedrawClick}
									/>
								)}
								<IconButtonV5
									IconComponent={ApplyIcon}
									title="Done"
									onClick={props.onApplyEditClick}
								/>
								<IconButtonV5
									IconComponent={CancelIcon}
									title="Cancel"
									onClick={props.onCancelEditClick}
								/>
							</>
						) : linkManager.isInLinkMode ? (
							<>
								<IconButtonV5
									IconComponent={SearchIcon}
									title="Find..."
									onClick={onFindXyiconClick}
								/>
								<IconButtonV5
									IconComponent={CancelIcon}
									title="Cancel"
									onClick={onCancelLinkClick}
								/>
							</>
						) : (
							<>
								<SelectedObjectsBubble selectedItems={selectedItems} />
								<IconButtonV5
									IconComponent={InfoIcon}
									title="Details"
									onClick={props.toggleDetailsPanel}
									isActive={props.isDetailsPanelOpen}
								/>
								<MarkupTypeChangerV5
									spaceViewRenderer={spaceViewRenderer}
									forceUpdate={forceUpdate}
								/>
								<Photo360PreviewButtonV5 spaceViewRenderer={spaceViewRenderer} />
								<FillButtonV5 spaceViewRenderer={spaceViewRenderer} />
								<LineThicknessChangerV5
									spaceViewRenderer={spaceViewRenderer}
									items={selectedItems}
								/>
								<ArrowHeadSizeChangerV5 spaceViewRenderer={spaceViewRenderer} />
								{props.isEditEnabled &&
									!isAnySelectedLocked &&
									(spaceItemController.boundaryManager.selectedItems.length === 1 ||
										(_isSingleMarkupSelected && doesMarkupKeepEditButtonInV5(singleMarkup?.type))) && (
										<IconButtonV5
											IconComponent={EditIcon}
											title="Edit"
											onClick={onEditClick}
										/>
									)}
								{areOnlyMarkupsSelected && firstColoredMarkup && (
									<ColorSelectorV5
										title="Markup color"
										color={{hex: firstColoredMarkup.color, transparency: 0}}
										onColorChange={(newColor: Color) => {
											onMarkupColorChange(spaceViewRenderer, newColor);
											forceUpdate();
										}}
										isTransparencyEnabled={false}
										eyeDropperProps={spaceViewRenderer.eyeDropperProps}
										outerDivRef={_ref}
										horizontalAlignment={HorizontalAlignment.outerRight}
										verticalAlignment={VerticalAlignment.bottom}
									/>
								)}
								{props.isLinkEnabled && hasPermissionToUpdate && (
									<IconButtonV5
										IconComponent={LinkIcon}
										title="Link"
										onClick={onLinkClick}
									/>
								)}
								{hasPermissionToUpdate && (
									<IconButtonV5
										IconComponent={CopyIcon}
										title="Copy"
										onClick={spaceViewRenderer.copySelectedItemsToClipboard}
									/>
								)}
								{doesSupportText && (
									<IconButtonV5
										IconComponent={TextIcon}
										title="Add Text"
										onClick={onAddTextClick}
									/>
								)}
								{hasPermissionToDelete && (
									<IconButtonV5
										IconComponent={DeleteIcon}
										title="Delete"
										onClick={props.onDeleteClick}
									/>
								)}
								{hasPermissionToUpdate && (
									<DropdownButtonV5
										className="SpaceActionBarDropdown"
										button={
											<IconButtonV5
												IconComponent={MoreIcon}
												title="More Tools"
												onClick={Functions.emptyFunction}
											/>
										}
										options={getMoreOptions(spaceViewRenderer, openMergeBoundariesWindow, onFlipXClick, onFlipYClick)}
									/>
								)}
							</>
						)}
					</SpaceActionBarStyled>
				);
			}}
		</Observer>
	);
});

SpaceActionBarV5.displayName = "SpaceActionBarV5";

const getMoreOptions = (
	spaceViewRenderer: SpaceViewRenderer,
	openMergeBoundariesWindow: () => void,
	onFlipXClick: () => void,
	onFlipYClick: () => void,
) => {
	const {spaceItemController} = spaceViewRenderer;
	const allItems = spaceItemController.selectedItems;
	const areOnlyXyiconsSelected = allItems.length === spaceViewRenderer.xyiconManager.selectedItems.length;
	const isAnyItemLockedIndividually = allItems.some((i) => i.isPositionLockedIndividually);
	const isAnyItemNotLockedIndividually = allItems.some((i) => !i.isPositionLockedIndividually);
	const isAnyItemLockedAnyWay = allItems.some((i) => i.isPositionLockedAnyWay);

	const moreOptions: IDropdownOption[] = [
		{
			label: "Copy to Stamp",
			onClick: spaceViewRenderer.copySelectedItemsToStamp,
			IconComponent: StampIcon,
		},
	];

	if (!isAnyItemLockedAnyWay) {
		moreOptions.push({
			label: "Cut",
			onClick: spaceViewRenderer.cutSelectedItemsToClipboard,
			IconComponent: ScissorsIcon,
		});
	}

	if (isAnyItemNotLockedIndividually) {
		moreOptions.push({
			label: "Lock Position",
			onClick: spaceViewRenderer.lockSpaceItemPosition,
			IconComponent: LockIcon,
			customElement: (
				<div className="shortcutOption">
					<span>Lock Position</span>
					<span className="shortcutText">Shift + L</span>
				</div>
			),
		});
	}

	if (isAnyItemLockedIndividually) {
		moreOptions.push({
			label: "Unlock Position",
			onClick: spaceViewRenderer.unlockSpaceItemPosition,
			IconComponent: UnlockIcon,
			customElement: (
				<div className="shortcutOption">
					<span>Unlock Position</span>
					<span className="shortcutText">Alt + Shift + L</span>
				</div>
			),
		});
	}

	if (areOnlyXyiconsSelected && !isAnyItemLockedAnyWay) {
		moreOptions.push(
			{
				label: "Flip Horizontal",
				onClick: onFlipXClick,
				IconComponent: FlipHorizontalIcon,
			},
			{
				label: "Flip Vertical",
				onClick: onFlipYClick,
				IconComponent: FlipVerticalIcon,
			},
			{
				label: "Unplot",
				onClick: () => onUnplotClick(spaceViewRenderer),
				IconComponent: UnplotIcon,
			},
		);
	}

	const {selectedItems} = spaceItemController;

	if (selectedItems.length > 0 && selectedItems.length === spaceItemController.boundaryManager.selectedItems.length) {
		moreOptions.push({
			label: "Merge",
			onClick: openMergeBoundariesWindow,
			IconComponent: MergeIcon,
		});
	}

	return moreOptions;
};

const hasPermission = (spaceViewRenderer: SpaceViewRenderer, permission: Permission): boolean => {
	return spaceViewRenderer.actions.someSpaceItemsHaveGivenPermission(spaceViewRenderer.spaceItemController.selectedItems, permission);
};

const onMarkupColorChange = (spaceViewRenderer: SpaceViewRenderer, newColor: Color) => {
	const selectedMarkups = (spaceViewRenderer.markupManager.selectedItems as Markup3D[]).filter((m) => MarkupsWithCustomizableColor.includes(m.type));

	for (const markup of selectedMarkups) {
		const modelData = markup.modelData as Markup;

		modelData.setColor(newColor.hex);
		markup.updateByModel(modelData);
	}

	_markupColorChangeDebouncer.debounce(() => {
		// Since we've already changed the modelData, and updated the model based on that,
		// it's necessary to pass "true" for the second (force) parameter here
		spaceViewRenderer.markupManager.updateItems(selectedMarkups, true);
	});
};
